// DayAgendaView.tsx — Tages-Agenda für die Mobile-Tagesansicht (`/termine`,
// SP6, Sub-Modus der Tagesansicht). Baut auf der Gruppierungs-/Format-Logik aus
// MyAgenda.tsx auf, aber genau für EINEN Tag und an den bereits gemergten +
// gefilterten Event-Strom aus dem CalendarStateContext (SP5) gekoppelt — KEIN
// eigenes Fetch. MERGED-Farbe = ev.source.color als linker Block-Rand
// (entspricht MyAgenda und OutlookKalender.eventPropGetter).

import React, { useMemo } from "react";
import { REFINED } from "../risikoanalyseStep/tokens";
import type { CalendarGridEvent } from "./types";

export interface DayAgendaViewProps {
    /** Genau dieser Tag (lokal). */
    date: Date;
    /** Bereits gemergt + gefiltert (aus dem Context). */
    events: CalendarGridEvent[];
    onSelectEvent?: (e: CalendarGridEvent) => void;
}

const pad = (n: number) => String(n).padStart(2, "0");

/** Lokaler Tages-Key (wie MyAgenda: getFullYear/getMonth/getDate). */
const localDayKey = (d: Date): string =>
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** 24h, deutsch, kein AM/PM. CalendarGridEvent.start/end sind bereits Date. */
const fmtTime = (d: Date): string =>
    d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", hour12: false });

export const DayAgendaView: React.FC<DayAgendaViewProps> = ({ date, events, onSelectEvent }) => {
    const dayKey = localDayKey(date);

    // Nur die Events DIESES Tages (lokaler Tag); nach Startzeit sortiert.
    // Ganztägige zuerst, dann zeitgebunden.
    const dayEvents = useMemo(() => {
        return events
            .filter((e) => localDayKey(e.start) === dayKey)
            .slice()
            .sort((a, b) => {
                if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
                return a.start.getTime() - b.start.getTime();
            });
    }, [events, dayKey]);

    if (dayEvents.length === 0) {
        return <div style={emptyStyle}>Keine Termine an diesem Tag</div>;
    }

    return (
        <div style={listStyle}>
            {dayEvents.map((ev) => (
                <button
                    key={`${ev.source.address}:${ev.id}`}
                    type="button"
                    onClick={() => onSelectEvent?.(ev)}
                    style={{ ...cardStyle, borderLeft: `3px solid ${ev.source.color}` }}
                >
                    <span style={titleStyle}>{ev.title || "(ohne Titel)"}</span>
                    <span style={metaStyle}>
                        {ev.allDay ? "Ganztägig" : `${fmtTime(ev.start)} – ${fmtTime(ev.end)}`}
                        {ev.location ? ` · ${ev.location}` : ""}
                    </span>
                </button>
            ))}
        </div>
    );
};

const listStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    overflowY: "auto",
    height: "100%",
    minHeight: 0,
    padding: "4px 0",
    boxSizing: "border-box",
};

const cardStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    textAlign: "left",
    width: "100%",
    minHeight: 44,
    padding: "10px 12px",
    background: REFINED.fieldBg,
    border: `1px solid ${REFINED.line}`,
    borderRadius: 10,
    cursor: "pointer",
    font: `inherit`,
    boxSizing: "border-box",
    overflowWrap: "anywhere",
    wordBreak: "break-word",
};

const titleStyle: React.CSSProperties = {
    font: `600 14.5px ${REFINED.fontUI}`,
    color: REFINED.ink,
};

const metaStyle: React.CSSProperties = {
    font: `500 12.5px ${REFINED.fontUI}`,
    color: REFINED.ink2,
};

const emptyStyle: React.CSSProperties = {
    padding: 20,
    textAlign: "center",
    color: REFINED.ink3,
    font: `500 14px ${REFINED.fontUI}`,
};

export default DayAgendaView;
